/**
 * Cart Quantity Stepper — plus / minus quantity control for cart drawer rows.
 *
 * Renders as a pill-shaped stepper: [ − ]  qty  [ + ]
 * Wires into the existing CartItems / CartDrawerItems updateQuantity flow.
 */

if (!customElements.get('cart-qty-stepper')) {
  customElements.define(
    'cart-qty-stepper',
    class CartQtyStepper extends HTMLElement {
      connectedCallback() {
        this.display = this.querySelector('.cart-qty-stepper__value');
        this.hiddenInput = this.querySelector('input[type="hidden"]');
        this.quantity = parseInt(this.display?.textContent, 10) || 1;

        this.handleClick = this.handleClick.bind(this);
        this.addEventListener('click', this.handleClick);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
      }

      handleClick(event) {
        const btn = event.target.closest('[data-action]');
        if (!btn) return;

        const action = btn.dataset.action;
        let newQty = this.quantity;

        if (action === 'plus') {
          newQty = this.quantity + 1;
        } else if (action === 'minus' && this.quantity > 1) {
          newQty = this.quantity - 1;
        }

        if (newQty === this.quantity) return;

        this.quantity = newQty;
        if (this.display) this.display.textContent = newQty;
        if (this.hiddenInput) this.hiddenInput.value = newQty;

        this.dispatchUpdate(newQty, event);
      }

      dispatchUpdate(quantity, event) {
        const index = this.dataset.index;
        const variantId = this.dataset.quantityVariantId;

        const cartItems =
          this.closest('cart-items') || this.closest('cart-drawer-items');
        if (!cartItems) return;

        cartItems.updateQuantity(index, quantity, event, 'updates[]', variantId);
      }
    }
  );
}

/**
 * Cart Remove Button — trash / delete button that removes a line item.
 *
 * Sets quantity to 0 via the same CartItems / CartDrawerItems updateQuantity flow.
 */

if (!customElements.get('cart-remove-btn')) {
  customElements.define(
    'cart-remove-btn',
    class CartRemoveBtn extends HTMLElement {
      connectedCallback() {
        this.handleClick = this.handleClick.bind(this);
        this.addEventListener('click', this.handleClick);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
      }

      handleClick(event) {
        const btn = event.target.closest('button');
        if (!btn) return;

        const index = btn.dataset.index;
        const variantId = btn.dataset.quantityVariantId;

        const cartItems =
          this.closest('cart-items') || this.closest('cart-drawer-items');
        if (!cartItems) return;

        cartItems.updateQuantity(index, 0, event, 'updates[]', variantId);
      }
    }
  );
}

/**
 * Cart Variant Select — dropdown variant selector for cart drawer rows.
 *
 * Overlays a native <select> on a styled pill. On change it swaps the
 * variant in the cart by adding the new variant first (so failure leaves
 * the cart unchanged), then removing the old line item. The remove
 * response includes rendered sections used to refresh the drawer.
 */

if (!customElements.get('cart-variant-select')) {
  customElements.define(
    'cart-variant-select',
    class CartVariantSelect extends HTMLElement {
      connectedCallback() {
        this.select = this.querySelector('select');
        this.display = this.querySelector('.cart-drawer-row__variant-display');
        if (!this.select) return;
        this.handleChange = this.handleChange.bind(this);
        this.select.addEventListener('change', this.handleChange);
      }

      disconnectedCallback() {
        this.select?.removeEventListener('change', this.handleChange);
      }

      async handleChange(event) {
        event.stopPropagation();
        const newVariantId = this.select.value;
        const currentVariantId = this.dataset.currentVariantId;
        const quantity = parseInt(this.dataset.currentQuantity, 10) || 1;
        const lineIndex = parseInt(this.dataset.lineIndex, 10);
        const sellingPlan = this.dataset.sellingPlan || null;

        if (newVariantId === currentVariantId) return;

        const selected = this.select.options[this.select.selectedIndex];
        if (this.display) this.display.textContent = selected.textContent.trim();

        const cartItems =
          this.closest('cart-items') || this.closest('cart-drawer-items');
        if (!cartItems) return;

        cartItems.enableLoading(lineIndex);

        try {
          /* 1. Add new variant first — if this fails the cart is unchanged */
          const addBody = { id: parseInt(newVariantId, 10), quantity };
          if (sellingPlan) addBody.selling_plan = parseInt(sellingPlan, 10);

          const addResponse = await fetch(routes.cart_add_url, {
            ...fetchConfig(),
            body: JSON.stringify(addBody),
          });

          if (!addResponse.ok) {
            throw new Error('Could not add variant');
          }

          /* 2. Remove old line item — include sections for re-render */
          const sections = cartItems
            .getSectionsToRender()
            .map((s) => s.section);

          const changeResponse = await fetch(routes.cart_change_url, {
            ...fetchConfig(),
            body: JSON.stringify({
              line: lineIndex,
              quantity: 0,
              sections,
              sections_url: window.location.pathname,
            }),
          });
          const state = await changeResponse.json();

          /* 3. Re-render cart drawer from the change response */
          cartItems.classList.toggle('is-empty', state.item_count === 0);
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer) {
            cartDrawer.classList.toggle('is-empty', state.item_count === 0);
          }

          cartItems.getSectionsToRender().forEach((section) => {
            const el =
              document.getElementById(section.id).querySelector(
                section.selector
              ) || document.getElementById(section.id);
            el.innerHTML = cartItems.getSectionInnerHTML(
              state.sections[section.section],
              section.selector
            );
          });
        } catch (error) {
          this.select.value = currentVariantId;
          if (this.display) {
            const reverted = this.select.options[this.select.selectedIndex];
            this.display.textContent = reverted.textContent.trim();
          }
        }

        cartItems.disableLoading(lineIndex);
      }
    }
  );
}

/**
 * Swap helper — adds a new variant and removes an old one, then
 * re-renders the cart drawer from the response sections.
 *
 * Uses variant-ID-based removal via /cart/update.js (not line-index),
 * matching the reference Asaya cart-offers pattern. Two network
 * requests total: one add, one update (which also returns sections).
 */

async function swapCartVariant({
  addVariantId,
  removeVariantId,
  quantity,
  sellingPlan,
  properties,
}) {
  const mainCart =
    document.getElementById('CartDrawer-CartItems') ||
    document.getElementById('main-cart-items');
  if (mainCart) mainCart.classList.add('cart__items--disabled');

  try {
    /* Step 1 — add the new variant */
    const addBody = { id: addVariantId, quantity };
    if (sellingPlan) addBody.selling_plan = sellingPlan;
    if (properties) addBody.properties = properties;

    const addRes = await fetch(routes.cart_add_url, {
      ...fetchConfig(),
      body: JSON.stringify(addBody),
    });
    if (!addRes.ok) throw new Error('Add failed');

    /* Step 2 — remove old variant by ID + fetch fresh sections */
    const updateRes = await fetch(routes.cart_update_url, {
      ...fetchConfig(),
      body: JSON.stringify({
        updates: { [removeVariantId]: 0 },
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname,
      }),
    });
    const state = await updateRes.json();

    /* Step 3 — re-render cart drawer from the update response */
    const drawerHtml = state.sections && state.sections['cart-drawer'];
    if (drawerHtml) {
      const parsed = new DOMParser().parseFromString(drawerHtml, 'text/html');
      ['cart-drawer-items', '.cart-drawer__footer'].forEach((sel) => {
        const target = document.querySelector(sel);
        const source = parsed.querySelector(sel);
        if (target && source) target.replaceWith(source);
      });
    }

    /* Step 4 — update cart icon bubble */
    const bubbleHtml = state.sections && state.sections['cart-icon-bubble'];
    if (bubbleHtml) {
      const bubbleEl = document.getElementById('cart-icon-bubble');
      if (bubbleEl) {
        const parsed = new DOMParser().parseFromString(bubbleHtml, 'text/html');
        const source = parsed.querySelector('.shopify-section');
        if (source) bubbleEl.innerHTML = source.innerHTML;
      }
    }

    /* Step 5 — update count badge + empty state */
    const badge = document.querySelector('.drawer__count-badge');
    if (badge) badge.textContent = state.item_count;
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.classList.toggle('is-empty', state.item_count === 0);
    }
  } catch (err) {
    console.error('[cart-swap]', err);
  } finally {
    const freshCart =
      document.getElementById('CartDrawer-CartItems') ||
      document.getElementById('main-cart-items');
    if (freshCart) freshCart.classList.remove('cart__items--disabled');
  }
}

/**
 * Cart Swap Upgrade — "Swap for better value" upsell card.
 *
 * Replaces the current line item with the upgrade variant.
 * Stores the original variant ID in a _swapped_from property for undo.
 */

if (!customElements.get('cart-swap-upgrade')) {
  customElements.define(
    'cart-swap-upgrade',
    class CartSwapUpgrade extends HTMLElement {
      connectedCallback() {
        this.handleClick = this.handleClick.bind(this);
        this.addEventListener('click', this.handleClick);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
      }

      handleClick(event) {
        event.preventDefault();
        event.stopPropagation();

        swapCartVariant({
          addVariantId: parseInt(this.dataset.upgradeVariantId, 10),
          removeVariantId: this.dataset.currentVariantId,
          quantity: parseInt(this.dataset.currentQuantity, 10) || 1,
          sellingPlan: this.dataset.sellingPlan
            ? parseInt(this.dataset.sellingPlan, 10)
            : null,
          properties: { _swapped_from: this.dataset.currentVariantId },
        });
      }
    }
  );
}

/**
 * Cart Undo Swap — reverts a "Swap for better value" action.
 *
 * Adds the original variant back (no properties, so no undo button
 * appears on the restored item) and removes the upgrade variant.
 */

if (!customElements.get('cart-undo-swap')) {
  customElements.define(
    'cart-undo-swap',
    class CartUndoSwap extends HTMLElement {
      connectedCallback() {
        this.handleClick = this.handleClick.bind(this);
        this.addEventListener('click', this.handleClick);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
      }

      handleClick(event) {
        event.preventDefault();
        event.stopPropagation();

        swapCartVariant({
          addVariantId: parseInt(this.dataset.originalVariantId, 10),
          removeVariantId: this.dataset.currentVariantId,
          quantity: parseInt(this.dataset.currentQuantity, 10) || 1,
          sellingPlan: this.dataset.sellingPlan
            ? parseInt(this.dataset.sellingPlan, 10)
            : null,
          properties: null,
        });
      }
    }
  );
}
